import React from 'react'
import styled from '@emotion/styled/macro'
import type { Color } from '~/types/Color'
import { OutlineButton } from '~/components/atoms/OutlineButton'
import { Dialog } from '~/components/atoms/Dialog'
import { samplePrompts } from '~/components/organisms/AICustomization/SamplePromptsSection'
import { DeviceSize } from '~/utils/deviceSize'
import { useClickWithDisabledReason } from '~/hooks/useClickWithDisabledReason'

const Section = styled.div`
  padding: 16px 16px 4px;
`

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: var(--bold-weight);
  color: var(--black);
`

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gutter-small);
`

const ExamplesLink = styled.button`
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  color: var(--teal-500);
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`

const SelectedColorsWrapper = styled.div`
  display: flex;
  align-items: center;
  background-color: #f8f8f8;
  border: 1px solid var(--gray-400);
  border-top: none;
  border-radius: 0 0 var(--gutter-small) var(--gutter-small);
  padding: 4px 8px;
  min-height: 32px;
`

const SelectedColorsContainer = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  white-space: nowrap;
  flex: 1;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`

const ColorChip = styled.div<{ backgroundColor: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px;
  border-radius: 16px;
  font-size: 12px;
  color: #333;
  flex-shrink: 0;

  &::before {
    content: '';
    display: inline-block;
    width: 15px;
    height: 15px;
    background-color: ${(props) => props.backgroundColor};
    border-radius: 50%;
    border: 1px solid rgba(0, 0, 0, 0.1);
  }
`

const TextAreaWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--gutter-small);
`

const TextArea = styled.textarea`
  width: 100%;
  min-height: 54px;
  padding: var(--gutter-small);
  border: 1px solid var(--gray-400);
  border-radius: var(--gutter-small) var(--gutter-small) 0 0;
  font-size: 16px;
  font-family: var(--default-family);
  line-height: 1.5;
  resize: vertical;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(80, 124, 107, 0.2);
  }

  &::placeholder {
    color: var(--text-sub);
  }

  &:disabled {
    background-color: var(--gray-100);
    color: var(--text-sub);
    cursor: not-allowed;
  }
`

const ButtonGroup = styled.div`
  display: flex;
  gap: var(--gutter-small);
  margin-top: var(--gutter-medium);

  @media (max-width: 768px) {
    display: none;
  }
`

const GenerateButton = styled(OutlineButton)`
  flex: 1;
`

const ResetButton = styled(OutlineButton)`
  background: var(--white);
  color: var(--text-sub);
  border: 1px solid var(--gray-400);

  &:hover:not(:disabled) {
    background: var(--gray-100);
    border-color: var(--line-gray);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

interface Props {
  prompt: string
  onPromptChange: (prompt: string) => void
  onGenerate: () => void
  onReset: () => void
  onRetry?: () => void
  isGenerating: boolean
  error: Error | null
  hasColorSelection?: boolean
  selectedColorIds?: Set<string>
  availableColors?: Color[]
  /**
   * インラインのボタン群（リセット/生成）を表示するかどうか。
   * サイドバーでは下部固定のフッターに移動するため false を渡す。
   */
  showInlineActions?: boolean
}

const PromptInputSectionComponent: React.FC<Props> = ({
  prompt,
  onPromptChange,
  onGenerate,
  onReset,
  onRetry,
  isGenerating,
  error,
  hasColorSelection = false,
  selectedColorIds,
  availableColors,
  showInlineActions = true
}) => {
  const canGenerate = prompt.trim() || hasColorSelection

  const [examplesOpen, setExamplesOpen] = React.useState(false)

  const [isMobile, setIsMobile] = React.useState(false)
  React.useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${DeviceSize.DESKTOP}px)`)
    const update = () => setIsMobile(mq.matches)
    update()
    // Modern browsers
    if ('addEventListener' in mq) {
      mq.addEventListener('change', update as EventListener)
    } else if ('addListener' in mq) {
      ;(mq as unknown as { addListener: (cb: () => void) => void }).addListener(
        update
      )
    }
    return () => {
      if ('removeEventListener' in mq) {
        mq.removeEventListener('change', update as EventListener)
      } else if ('removeListener' in mq) {
        ;(
          mq as unknown as { removeListener: (cb: () => void) => void }
        ).removeListener(update)
      }
    }
  }, [])

  // 選択中の色を取得
  const selectedColors = React.useMemo(() => {
    if (!selectedColorIds || !availableColors) return []
    return availableColors.filter((color) =>
      selectedColorIds.has(String(color.id))
    )
  }, [selectedColorIds, availableColors])

  return (
    <Section>
      <TextAreaWrapper>
        <LabelRow>
          <Label htmlFor="prompt-input">あなたのイメージを教えてください</Label>
          <ExamplesLink type="button" onClick={() => setExamplesOpen(true)}>
            例文から選ぶ
          </ExamplesLink>
        </LabelRow>
        <TextArea
          id="prompt-input"
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          placeholder="例）20代後半の男性へのプレゼント。海を連想させるようなデザインにしてください。"
          disabled={isGenerating}
          aria-describedby={error ? 'prompt-error' : undefined}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault()
              if (error && onRetry) {
                onRetry()
              } else {
                onGenerate()
              }
            }
          }}
        />
      </TextAreaWrapper>

      <Dialog
        opened={examplesOpen}
        onClose={() => setExamplesOpen(false)}
        title="例文一覧"
        fullscreen={isMobile}
      >
        <div style={{ padding: '12px 16px' }}>
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 8
            }}
          >
            {samplePrompts.map((s) => (
              <li key={s.label}>
                <button
                  type="button"
                  onClick={() => {
                    onPromptChange(s.prompt)
                    setExamplesOpen(false)
                  }}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 12px',
                    border: '1px solid var(--gray-300)',
                    borderRadius: 8,
                    background: 'var(--white)',
                    cursor: 'pointer',
                    fontSize: 12
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>
                    {s.label}
                  </div>
                  <div style={{ color: 'var(--text-sub)' }}>{s.prompt}</div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </Dialog>

      <SelectedColorsWrapper>
        <SelectedColorsContainer>
          {selectedColors.length > 0 ? (
            selectedColors.map((color) => (
              <ColorChip key={color.id} backgroundColor={color.colorCode}>
                {color.name}
              </ColorChip>
            ))
          ) : (
            <span style={{ fontSize: '12px', color: 'var(--text-sub)' }}>
              使いたい色を選べます
            </span>
          )}
        </SelectedColorsContainer>
      </SelectedColorsWrapper>

      {showInlineActions && (
        <ButtonGroup>
          <ResetButton onClick={onReset} disabled={isGenerating}>
            リセット
          </ResetButton>
          {(() => {
            const common = useClickWithDisabledReason({
              disabled: isGenerating,
              ariaDisabled: !isGenerating && !canGenerate,
              reason: !canGenerate
                ? {
                    type: 'validation_error',
                    message:
                      'プロンプトを入力するか、色を1つ以上選んでください。'
                  }
                : null,
              onClick: error && onRetry ? onRetry : onGenerate
            })
            return (
              <GenerateButton
                {...common.buttonProps}
                label={error && onRetry ? '再試行' : 'AIでデザインを生成'}
              >
                {isGenerating
                  ? '生成中...'
                  : error && onRetry
                  ? '再試行'
                  : 'AIでデザインを生成'}
              </GenerateButton>
            )
          })()}
        </ButtonGroup>
      )}
    </Section>
  )
}

export const PromptInputSection = React.memo(PromptInputSectionComponent)
